import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { IntegrationSystem } from '@/shared/types';

import { reformatDocumentTypeToEntity } from '@/helpers/reformatDocumentTypeToEntity';

import { queryKeys } from '@/api/queryKeys';
import { DocumentType, DocumentTypeEntity } from '@/api/types';

import api from '@/api';

interface UseERZDocumentTypesOptions {
  allowedDocumentTypeCodes?: Array<DocumentType<IntegrationSystem.ERZ>['code']>;
  disallowedDocumentTypeKinds?: Array<DocumentType<IntegrationSystem.ERZ>['kind']>;
  disallowedDocumentTypeCodes?: Array<DocumentType<IntegrationSystem.ERZ>['code']>;
}

interface UseERZDocumentTypesResult {
  data: Array<DocumentTypeEntity<IntegrationSystem.ERZ>>;
  isLoading: boolean;
}

export const useERZDocumentTypes = ({
  allowedDocumentTypeCodes = [],
  disallowedDocumentTypeKinds = [],
  disallowedDocumentTypeCodes = [],
}: UseERZDocumentTypesOptions = {}): UseERZDocumentTypesResult => {
  const { data: rawDocumentTypes = [], isLoading } = useQuery({
    queryKey: queryKeys.erz.documentTypes(),
    queryFn: () => api.erz.dictionary.getDocumentTypes().then((r) => r.data),
  });

  const allowedCodesKey = JSON.stringify(allowedDocumentTypeCodes);
  const disallowedKindsKey = JSON.stringify(disallowedDocumentTypeKinds);
  const disallowedCodesKey = JSON.stringify(disallowedDocumentTypeCodes);

  const data = useMemo(() => {
    let documentTypes = rawDocumentTypes;

    if (allowedDocumentTypeCodes.length) {
      documentTypes = allowedDocumentTypeCodes.reduce(
        (accumulator, documentTypeCode) => {
          const wantedDocumentType = rawDocumentTypes.find(
            (documentType) => documentType.code !== null && documentType.code === documentTypeCode,
          );
          return isEmpty(wantedDocumentType) ? accumulator : [...accumulator, wantedDocumentType];
        },
        [] as Array<DocumentType<IntegrationSystem.ERZ>>,
      );
    }

    if (disallowedDocumentTypeKinds.length || disallowedDocumentTypeCodes.length) {
      documentTypes = documentTypes.filter(
        (documentType) =>
          !disallowedDocumentTypeKinds.includes(documentType.kind) &&
          !disallowedDocumentTypeCodes.includes(documentType.code),
      );
    }

    return documentTypes.map(reformatDocumentTypeToEntity);
  }, [rawDocumentTypes, allowedCodesKey, disallowedKindsKey, disallowedCodesKey]);

  return { data, isLoading };
};
